import { ReactNode, useCallback } from 'react'
import { formatDuration } from '~/helpers/dateHelper'
import { useAnalytics } from '~/modules/analytics/hooks/useAnalytics'
import { AnalyticsQueryFilterContext } from '~/modules/analytics/utils'

import { AnalyticsDataset } from '@hap/analytics'
import { Icon, cn } from '@hap/components'
import { commafy } from '@hap/utils'
import { toPercent } from '@hap/utils'

import { MetricCellBase, MetricVariants } from './base'

export function MetricCell(
  props: {
    className?: string
    queryContext: AnalyticsQueryFilterContext
    field:
      | 'handledBy'
      | 'handledByIds'
      | 'id'
      | 'handleTime'
      | 'initialCustomerSentimentScore'
      | 'medialCustomerSentimentScore'
      | 'closingCustomerSentimentScore'
      | 'averageEvaluationScore'
      | 'sentimentInfluence'
      | 'totalDeadAirTime'
      | 'totalHolds'
      | 'score'

    label: string
    operation?: 'countDistinct' | 'avg' | 'sum'
    format?: 'duration' | 'percent' | ((value: string) => string | number)
    dataset?: AnalyticsDataset
    positive?: 'negative' | 'positive'
    precision?: number
    drilldown?: boolean
    emptyValue?: ReactNode
  } & MetricVariants,
) {
  const positive = props.positive || 'positive'
  const { data, loading } = useAnalytics({
    timespan: props.queryContext.timespan,
    filters: props.queryContext.filters,
    aggregations: {
      count: {
        op: props.operation || 'countDistinct',
        field: props.field,
        trends: true,
        precision: props.precision || 0,
      },
    },
    dataset: props.dataset || 'interactions',
  })

  const percentChange = data && data[0] ? data[0].count_percent_change : null
  const isUp = percentChange !== null && percentChange > 0

  const renderValue = useCallback(
    (d: typeof data) => {
      if (!d || !d[0])
        return (
          props.emptyValue || (
            <div className="bg-secondary h-6 max-w-[100px] rounded-md"></div>
          )
        )
      if (typeof props.format === 'function') {
        return props.format(d[0].count)
      }

      switch (props.format) {
        case 'duration':
          return formatDuration(parseInt(d[0].count))
        case 'percent':
          return toPercent(d[0].count)
        default:
          return commafy(parseFloat(d[0].count)) || 0
      }
    },
    [props],
  )

  const renderChange = useCallback(
    (d: typeof data) => {
      if (!d || !d[0]) return <div className=""></div>
      return (
        <div
          className={cn('inline-flex items-center rounded-md text-sm', {
            'text-green':
              percentChange !== 0 &&
              ((positive === 'positive' && isUp) || (positive === 'negative' && !isUp)),
            'text-red':
              percentChange !== 0 &&
              ((positive === 'positive' && !isUp) || (positive === 'negative' && isUp)),
          })}
        >
          <Icon
            name="triangle"
            weight="fill"
            className={cn('h-3', {
              'fill-green':
                percentChange !== 0 &&
                ((positive === 'positive' && isUp) || (positive === 'negative' && !isUp)),
              'fill-red rotate-180':
                percentChange !== 0 &&
                ((positive === 'positive' && !isUp) || (positive === 'negative' && isUp)),
            })}
          />
          {d[0].count_percent_change === 0 ? '–' : toPercent(d[0].count_percent_change)}
        </div>
      )
    },
    [positive, isUp, percentChange],
  )

  return (
    <MetricCellBase
      className={props.className}
      variant={props.variant}
      label={props.label}
      value={renderValue(data)}
      loading={loading}
      drilldown={props.drilldown}
      trend={renderChange(data)}
    />
  )
}
